import { useRouter } from "next/router";
import { ReactElement, useCallback, useEffect, useState } from "react";
import { useForm, type UseFormRegister } from "react-hook-form";
import { isEmail } from "@common/utils/string";
import {
  Anchor,
  Box,
  Button,
  Card,
  Center,
  Loader,
  Stack,
  Text,
  TextInput,
  useMantineTheme,
} from "@mantine/core";
import CenteredLayout from "@common/components/thirdParty/mantine/layout/CenteredLayout";
import ErrorMessage from "@common/components/text/ErrorMessage";
import PageTitle from "@common/components/thirdParty/mantine/text/PageTitle";
import ButtonAsText from "@common/components/thirdParty/mantine/text/ButtonAsText";
import Form from "@common/components/ui/form/Form";
import {
  persistUTMQueryParamsToString,
  persistUTMQueryParamsToUrlObject,
  safeRedirectPath,
} from "@common/utils/router";
import { DEFAULT_ERROR_MESSAGE } from "@common/utils/use-query/constants";
import MigrationCard from "@components/forms/instance/MigrationCard";
import useIsValidMigrationId from "@hooks/useIsValidMigrationId";
import { useConfig } from "@stores/config/hooks";
import { areMagicLinksAllowed } from "@stores/config/utils";
import { magicLinkStore } from "@stores/magic-link";
import { sessionStore } from "@stores/session";
import { ILoginDetails } from "@stores/session/types";
import Link from "next/link";

type LoginFormType = "password" | "magic" | null;

type LoginErrorLike =
  | {
      message?: string;
      status?: string | number;
    }
  | null
  | undefined;

function LoginCardHeader({ isFormLoading }: { isFormLoading: boolean }) {
  return (
    <Box p="xl">
      <Stack spacing="md">
        <PageTitle m={0}>Log in to your Metabase account</PageTitle>

        {isFormLoading ? (
          <Center py="xl">
            <Loader />
          </Center>
        ) : null}
      </Stack>
    </Box>
  );
}

function LoginFormBody({
  areMagicLinksEnabled,
  cloudLoginHref,
  error,
  forgotPasswordHref,
  isInitiatingMagicLogin,
  isMagicForm,
  isPendingLogin,
  isPasswordForm,
  isValid,
  onContinueWithPassword,
  onContinueWithMagicLink,
  onResetErrors,
  onSubmit,
  register,
}: {
  areMagicLinksEnabled: boolean;
  cloudLoginHref: string;
  error: LoginErrorLike;
  forgotPasswordHref: ReturnType<typeof persistUTMQueryParamsToUrlObject>;
  isInitiatingMagicLogin: boolean;
  isMagicForm: boolean;
  isPendingLogin: boolean;
  isPasswordForm: boolean;
  isValid: boolean;
  onContinueWithPassword: () => void;
  onContinueWithMagicLink: () => void;
  onResetErrors: () => void;
  onSubmit: () => void;
  register: UseFormRegister<ILoginDetails>;
}) {
  return (
    <Box px="xl" pb="xl">
      <Form onSubmit={onSubmit}>
        <Stack spacing="xl">
          <TextInput
            id="email"
            type="email"
            label="Email address"
            placeholder="almost-there@company.com"
            onInput={onResetErrors}
            {...register("email", {
              required: true,
              validate(value) {
                return isEmail(value) || "Invalid email";
              },
            })}
          />

          {isMagicForm && (
            <MagicLinkForm
              areMagicLinksEnabled={areMagicLinksEnabled}
              error={error}
              isLoading={isPendingLogin || isInitiatingMagicLogin}
              isValid={isValid}
              onContinueWithPassword={onContinueWithPassword}
            />
          )}

          {isPasswordForm && (
            <PasswordLoginForm
              cloudLoginHref={cloudLoginHref}
              error={error}
              forgotPasswordHref={forgotPasswordHref}
              isLoading={isPendingLogin}
              isValid={isValid}
              onContinueWithMagicLink={onContinueWithMagicLink}
              register={register}
            />
          )}
        </Stack>
      </Form>
    </Box>
  );
}

function MagicLinkForm({
  areMagicLinksEnabled,
  error,
  isLoading,
  isValid,
  onContinueWithPassword,
}: {
  areMagicLinksEnabled: boolean;
  error: LoginErrorLike;
  isLoading: boolean;
  isValid: boolean;
  onContinueWithPassword: () => void;
}) {
  return (
    <Stack spacing="md">
      <Button
        fullWidth
        size="md"
        type="submit"
        disabled={!isValid || isLoading}
        loading={isLoading}
      >
        Get a magic link to sign in
      </Button>

      {error && <ErrorMessage>{error.message || error.status}</ErrorMessage>}

      {areMagicLinksEnabled && (
        <ButtonAsText
          c="brand.4"
          ta="center"
          fw="bold"
          lh="22px"
          onClick={onContinueWithPassword}
          sx={{
            border: "none",
            ["&:hover"]: { border: "none" },
          }}
        >
          Log in with password instead
        </ButtonAsText>
      )}
    </Stack>
  );
}

function PasswordLoginForm({
  cloudLoginHref,
  error,
  forgotPasswordHref,
  isLoading,
  isValid,
  onContinueWithMagicLink,
  register,
}: {
  cloudLoginHref: string;
  error: LoginErrorLike;
  forgotPasswordHref: ReturnType<typeof persistUTMQueryParamsToUrlObject>;
  isLoading: boolean;
  isValid: boolean;
  onContinueWithMagicLink: () => void;
  register: UseFormRegister<ILoginDetails>;
}) {
  return (
    <Stack spacing="md">
      <Stack spacing="sm">
        <TextInput
          id="password"
          type="password"
          label="Password"
          placeholder="Shh..."
          {...register("password", {
            required: true,
            shouldUnregister: true,
          })}
        />

        <Text fz="sm">
          <Link href={forgotPasswordHref}>Forgot password?</Link>
        </Text>

        {error && error.message !== "404" && (
          <ErrorMessage>
            {error.message || error.status || DEFAULT_ERROR_MESSAGE}
            {error.status === 401 && (
              <>
                &nbsp;You might also be{" "}
                <Anchor
                  href={cloudLoginHref}
                  target="_blank"
                  rel="noreferrer"
                  title="Metabase - Your instance"
                >
                  looking for your instance
                </Anchor>
                ?
              </>
            )}
          </ErrorMessage>
        )}

        <Button
          fullWidth
          size="md"
          type="submit"
          disabled={!isValid || isLoading}
          loading={isLoading}
        >
          Log in
        </Button>
      </Stack>
      <ButtonAsText
        c="brand.4"
        ta="center"
        fw="bold"
        lh="22px"
        onClick={onContinueWithMagicLink}
        sx={{
          border: "none",
          ["&:hover"]: { border: "none" },
        }}
      >
        Log in with a magic link instead
      </ButtonAsText>
    </Stack>
  );
}

function MagicLinkRequestedCard({
  email,
  onBackToLogin,
}: {
  email?: string;
  onBackToLogin: () => void;
}) {
  return (
    <Box p="xl">
      <Stack align="center" spacing="sm">
        <Box h={28} w="100%" />
        <Stack spacing="xs">
          <PageTitle m={0} ta="center" lh="28px">
            Check your inbox
          </PageTitle>
          <Stack spacing="sm">
            <Text c="gray.4" fz="sm" ta="center" lh="20px" component="p" my="0">
              If you have a Metabase account, a login link is on its way to{" "}
              {email}
            </Text>
            <Box h={44} w="100%" />
          </Stack>
        </Stack>
        <ButtonAsText c="brand.4" lh="22px" fw="bold" onClick={onBackToLogin}>
          Back to login
        </ButtonAsText>
      </Stack>
    </Box>
  );
}

function LoginFooterCta() {
  const router = useRouter();
  const { query } = router;
  const { colors } = useMantineTheme();
  const checkoutHref = persistUTMQueryParamsToString(router, "/checkout", {
    ...query,
    continue: 1,
  });
  return (
    <Center p="xl" sx={{ borderTop: `1px solid ${colors.gray[3]}` }}>
      <Text c="gray.4" fz="sm" ta="center">
        <a href={checkoutHref}>Create an account</a> to get started with
        Metabase.
      </Text>
    </Center>
  );
}

export default function LoginPage(): ReactElement {
  const config = useConfig();
  const areMagicLinksEnabled = areMagicLinksAllowed(config);

  const { migrationId } = useIsValidMigrationId();

  const [isMagicLinkRequested, setIsMagicLinkRequested] = useState(false);
  const [loginFormType, setLoginFormType] = useState<LoginFormType>(
    areMagicLinksEnabled && !migrationId ? "magic" : "password",
  );

  useEffect(() => {
    if (migrationId && loginFormType === "magic") {
      setLoginFormType("password");
    }
  }, [loginFormType, migrationId]);

  const router = useRouter();
  const { query } = router;

  const isPasswordForm = loginFormType === "password";
  const isMagicForm = loginFormType === "magic";

  const {
    isPending: isPendingLogin,
    error: loginError,
    mutate: login,
    reset: resetLogin,
  } = sessionStore.useLogin();

  const {
    isPending: isInitiatingMagicLogin,
    mutate: initiateMagicLogin,
    reset: resetInitiateMagicLogin,
  } = magicLinkStore.useInitiateMagicLinkLogin();

  const {
    register,
    watch,
    formState: { isValid },
    handleSubmit,
  } = useForm<ILoginDetails>({
    shouldFocusError: true,
    mode: "onChange",
    reValidateMode: "onChange",
  });

  const watchEmail = watch("email");

  const redirect_url = safeRedirectPath(query.redirectTo);

  const onSubmit = useCallback(
    ({ email, password }: ILoginDetails) => {
      if (isMagicForm) {
        initiateMagicLogin(
          { email, redirect_url },
          {
            onSuccess() {
              setIsMagicLinkRequested(true);
            },
          },
        );
      }

      if (isPasswordForm) {
        login({ email, password });
      }
    },
    [login, isMagicForm, initiateMagicLogin, isPasswordForm, redirect_url],
  );

  const isFormLoading = loginFormType === null;
  const shouldRenderForm = !isFormLoading && !isMagicLinkRequested;

  const resetErrors = () => {
    resetInitiateMagicLogin();
    resetLogin();
  };

  const forgotPasswordHref = persistUTMQueryParamsToUrlObject(
    router,
    "/forgot-password",
  );
  const cloudLoginHref = persistUTMQueryParamsToString(
    router,
    "https://www.metabase.com/cloud/login",
  );

  return (
    <CenteredLayout.New wordmark maxWidth={420}>
      <Stack align="center" spacing="md" w="100%">
        <MigrationCard w="100%" />

        <Card
          withBorder
          radius="md"
          shadow="sm"
          p={0}
          style={{ overflow: "hidden" }}
          w="100%"
        >
          {isMagicLinkRequested ? (
            <MagicLinkRequestedCard
              email={watchEmail}
              onBackToLogin={() => setIsMagicLinkRequested(false)}
            />
          ) : (
            <>
              <LoginCardHeader isFormLoading={isFormLoading} />

              {!isFormLoading && shouldRenderForm && (
                <LoginFormBody
                  areMagicLinksEnabled={areMagicLinksEnabled}
                  cloudLoginHref={cloudLoginHref}
                  error={loginError}
                  forgotPasswordHref={forgotPasswordHref}
                  isInitiatingMagicLogin={isInitiatingMagicLogin}
                  isMagicForm={isMagicForm}
                  isPendingLogin={isPendingLogin}
                  isPasswordForm={isPasswordForm}
                  isValid={isValid}
                  onContinueWithPassword={() => {
                    resetErrors();
                    setLoginFormType("password");
                  }}
                  onContinueWithMagicLink={() => {
                    resetErrors();
                    setLoginFormType("magic");
                  }}
                  onResetErrors={resetErrors}
                  onSubmit={handleSubmit(onSubmit)}
                  register={register}
                />
              )}

              {!isFormLoading && shouldRenderForm && areMagicLinksEnabled && (
                <LoginFooterCta />
              )}
            </>
          )}
        </Card>
      </Stack>
    </CenteredLayout.New>
  );
}

LoginPage.hideNav = true;
